import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Accordion, AccordionTab, AppTable, Button, Dialog, InputNumber, InputSearch, InputText, ProgressSpinner, TabPanel, TabView, Tooltip, type TooltipRef } from '@allygpo/ally-ux';
import { getCoreRowModel, getSortedRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import ApiService, { handleApiError } from '../../services/api';
import { FeeSchedule } from '../../types';
import FeeScheduleUpload, { type FeeUploadFooterActions } from './FeeScheduleUpload';
import { useToast } from '../../context/ToastContext';
import { usePracticeData } from '../../hooks/usePracticeData';
import { alignEnd, confirm, headerEnd, ModalHeading, sortableAs, tableView, unpaginated } from '../../components/ui';

// Normalize a payer name for matching (lowercase + strip spaces). Mirrors how the payer
// mix / drug-payer matrix keys line up with fee-schedule row names.
const normPayer = (s: string): string => String(s || '').trim().toLowerCase().replace(/\s+/g, '');

// Published-limit multipliers are compared at the precision the input can express
// (4 decimals, matching the InputNumber's maxFractionDigits). This is what keeps
// 1.5 -> 1.5000, or any nudge below the 4th decimal, from counting as an edit:
// neither can move a rate the user could have entered by hand.
const quantizeMultiplier = (v: number | null | undefined): number =>
  Math.round((Number(v) || 0) * 10000);

const formatMultiplier = (v: number) => `${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}×`;

/** What the Published limits grid cells read from `meta.view`. Rows are master payer group names. */
interface MultiplierGridView {
  multiplierByGroup: Record<string, number>;
  savedMultiplierByGroup: Record<string, number>;
  setMultiplier: (group: string, value: number) => void;
  disabled: boolean;
}

// Medicare's rate is the published limit itself, so its multiplier is fixed at 1.00×.
const isMedicareGroup = (group: string) => group.trim().toLowerCase() === 'medicare';

const isMultiplierChanged = (group: string, view: MultiplierGridView) =>
  quantizeMultiplier(view.multiplierByGroup[group]) !== quantizeMultiplier(view.savedMultiplierByGroup[group]);

// Column sizes are minimums in AppTable's fixed layout (the table stretches them to fill), so they
// add up to less than the narrowest the tab gets.
const multiplierColumns: ColumnDef<string>[] = [
  {
    id: 'group',
    accessorFn: (group) => group,
    ...sortableAs('text'),
    size: 300,
    header: 'Master payer group',
    cell: ({ row: { original: group }, table }) => (
      <span className="flex align-items-center u-gap-2 min-w-0 flex-wrap">
        <span className="flex-none font-medium text-default text-xs">{group}</span>
        {isMultiplierChanged(group, tableView<MultiplierGridView, string>(table)) && <span className="setup-changed text-2xs font-semibold text-selected white-space-nowrap">Changed</span>}
      </span>
    ),
  },
  {
    id: 'applied',
    size: 110,
    header: () => headerEnd('Currently applied'),
    meta: alignEnd,
    cell: ({ row: { original: group }, table }) => {
      const saved = tableView<MultiplierGridView, string>(table).savedMultiplierByGroup[group];
      if (isMedicareGroup(group)) return <span className="text-secondary">{formatMultiplier(1)}</span>;
      return <span className="text-secondary">{quantizeMultiplier(saved) > 0 ? formatMultiplier(saved) : 'Not set'}</span>;
    },
  },
  {
    id: 'multiplier',
    size: 130,
    header: () => headerEnd('New multiplier'),
    meta: { headerStyle: { textAlign: 'end' as const, paddingInlineStart: 'var(--a-spacing-6)' } },
    cell: ({ row: { original: group }, table }) => {
      const { multiplierByGroup, setMultiplier, disabled } = tableView<MultiplierGridView, string>(table);
      if (isMedicareGroup(group)) {
        return <span className="block text-right text-secondary u-pr-3 multiplier-locked" data-pr-tooltip="Medicare uses the published limit as is">{formatMultiplier(1)}</span>;
      }
      return (
        <span className="block">
          <InputNumber
            suffix=" ×"
            inputClassName="w-full text-right"
            value={multiplierByGroup[group] ?? null}
            onValueChange={(e) => setMultiplier(group, e.value ?? 0)}
            mode="decimal"
            minFractionDigits={2}
            maxFractionDigits={4}
            min={0}
            max={10}
            step={0.05}
            placeholder="—"
            className="master-payor-rule-input w-full"
            disabled={disabled}
            aria-label={`New published-limit multiplier for ${group}`}
          />
        </span>
      );
    },
  },
];

/** What the Manage rates grid cells read from `meta.view`. Rows are payer names. */
interface RateGridView {
  editedRates: Record<string, number>;
  originalRates: Record<string, number>;
  /** Normalized payer -> checked in payer preferences; null until preferences load. */
  payerCheckedLookup: Map<string, boolean> | null;
  medicareLimit: number | null;
  onRateChange: (payer: string, value: number | null) => void;
}

const isRateChanged = (payer: string, view: RateGridView) => (view.originalRates[payer] ?? 0) !== (view.editedRates[payer] ?? 0);
// No rate (missing or $0) falls back to the Medicare rate.
const usesMedicare = (value: number | undefined) => value === undefined || value === null || value === 0;

const rateColumns: ColumnDef<string>[] = [
  {
    id: 'payer',
    size: 185,
    header: 'Master payer group',
    cell: ({ row: { original: payer }, table }) => {
      const view = tableView<RateGridView, string>(table);
      // Checked/unchecked in payer preferences for the selected PF; no dot until preferences load.
      const checked = view.payerCheckedLookup ? (view.payerCheckedLookup.get(normPayer(payer)) ?? true) : null;
      return (
        <span className="fee-schedule-payer-name text-xs flex align-items-start u-gap-2 font-medium">
          {/* Only the exception is marked: a group left out of payer preferences for this family. */}
          {checked === false && (
            <span
              className="payer-pref-dot"
              data-pr-tooltip={checked
                ? 'Checked in payer preferences for this product family'
                : 'Unchecked in payer preferences — excluded for this product family'}
              data-pr-position="top"
              style={{
                display: 'inline-block',
                width: 9,
                height: 9,
                borderRadius: '50%',
                marginTop: '0.25rem',
                verticalAlign: 'middle',
                flexShrink: 0,
                background: checked ? 'rgba(34,197,94,0.28)' : 'rgba(239,68,68,0.22)',
                border: checked ? '1px solid rgba(22,163,74,0.55)' : '1px solid rgba(220,38,38,0.5)',
                cursor: 'default',
              }}
            />
          )}
          <span>
            {payer}
            {isRateChanged(payer, view) && <span className="setup-changed text-2xs font-semibold text-selected white-space-nowrap u-ml-2">Changed</span>}
          </span>
        </span>
      );
    },
  },
  {
    id: 'rate',
    size: 100,
    header: () => headerEnd('Rate / billing unit'),
    meta: alignEnd,
    cell: ({ row: { original: payer }, table }) => {
      const { editedRates, onRateChange } = tableView<RateGridView, string>(table);
      return (
        // No rate (falls back to Medicare) shows empty with a dash, not "$0.00", which read as a $0 rate.
        <InputNumber
          value={usesMedicare(editedRates[payer]) ? null : editedRates[payer]}
          onValueChange={(e) => onRateChange(payer, e.value ?? null)}
          placeholder="—"
          inputClassName="w-full text-right"
          mode="currency"
          currency="USD"
          locale="en-US"
          minFractionDigits={4}
          maxFractionDigits={4}
          min={0}
          max={999999.9999}
          className="fee-schedule-rate-input w-full"
          aria-label={`Rate per billing unit for ${payer}`}
        />
      );
    },
  },
  {
    id: 'ratio',
    size: 85,
    header: () => headerEnd('vs Medicare'),
    meta: alignEnd,
    cell: ({ row: { original: payer }, table }) => {
      const { editedRates, medicareLimit } = tableView<RateGridView, string>(table);
      const val = editedRates[payer];
      if (usesMedicare(val)) {
        return (
          <span className="fee-rate-ratio text-secondary text-xs fee-fallback-note" data-pr-tooltip="No rate entered: this payer falls back to the Medicare rate in weighted NCR" data-pr-position="top">
            Uses Medicare
          </span>
        );
      }
      return <span className="fee-rate-ratio text-secondary text-xs">{medicareLimit ? `${(Number(val) / medicareLimit).toFixed(2)}×` : '—'}</span>;
    },
  },
];

interface FeeScheduleModalProps {
  visible: boolean;
  accountNumber: string;
  masterPayorGroups: string[];
  onHide: () => void;
}

interface CategoryGroup {
  category: string;
  items: FeeSchedule[];
}

const FeeScheduleModal: React.FC<FeeScheduleModalProps> = ({
  visible,
  accountNumber,
  masterPayorGroups,
  onHide,
}) => {
  const { isDemoMode } = usePracticeData();
  const [feeSchedules, setFeeSchedules] = useState<FeeSchedule[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [editedRates, setEditedRates] = useState<Record<string, number>>({});
  const [originalRates, setOriginalRates] = useState<Record<string, number>>({});
  const [activeTab, setActiveTab] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterText, setFilterText] = useState('');

  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [categoryFilter, setCategoryFilter] = useState('');
  const [uploadHasChanges, setUploadHasChanges] = useState(false);
  const [uploadFooter, setUploadFooter] = useState<FeeUploadFooterActions | null>(null);
  // Published-limit multipliers, per master payer group, for the "Published Limits" tab.
  // Each group's eligible-drug rates become Medicare published payment limit × multiplier.
  const [multiplierByGroup, setMultiplierByGroup] = useState<Record<string, number>>({});
  // The multipliers as last pulled from (or written to) the server. Apply All stays
  // disabled until the entered values actually differ from these.
  const [savedMultiplierByGroup, setSavedMultiplierByGroup] = useState<Record<string, number>>({});
  const [isApplyingAll, setIsApplyingAll] = useState(false);
  // Payer preferences (drug-payer matrix) per category -> pfId -> payer -> checked.
  // Powers the green/red "checked in payer preferences" dot next to each payer name.
  const [prefsByCategory, setPrefsByCategory] = useState<Record<string, Record<string, Record<string, boolean>>>>({});
  const [prefsLoaded, setPrefsLoaded] = useState<Record<string, boolean>>({});
  const deleteDialogOpenRef = useRef(false);
  // PrimeReact's Tooltip only binds its target listeners from its SECOND render
  // onward (loadTargetEvents sits in a useUpdateEffect), so a Tooltip that mounts
  // in the same commit as its targets never attaches. Held so we can rebind by hand.
  const payerTipRef = useRef<TooltipRef>(null);
  const toast = useToast();

  const loadFeeSchedules = useCallback(async () => {
    if (!accountNumber) {
      setError('No active account selected.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await ApiService.getFeeSchedules(accountNumber);
      setFeeSchedules(response.fee_schedules);
      setSelectedId(null);
      setEditedRates({});
      setOriginalRates({});
      setExpandedCategories(new Set());
    } catch (err) {
      setFeeSchedules([]);
      setSelectedId(null);
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  }, [accountNumber]);

  useEffect(() => {
    if (!visible || !accountNumber) return;
    loadFeeSchedules();
    setCategoryFilter('');
    setActiveTab(0);
    setUploadHasChanges(false);
  }, [visible, accountNumber, loadFeeSchedules]);

  // Repopulate each group's saved published-limit multiplier so the "Published Limits"
  // tab reflects what's currently applied. Best-effort — never blocks the modal.
  useEffect(() => {
    if (!visible || !accountNumber) return;
    let cancelled = false;
    (async () => {
      try {
        const saved = await ApiService.getPublishedLimitMultipliers(accountNumber);
        if (cancelled) return;
        const byGroup: Record<string, number> = {};
        for (const rule of saved.rules) {
          if (rule.master_payor_group && rule.multiplier > 0) {
            byGroup[rule.master_payor_group] = rule.multiplier;
          }
        }
        setMultiplierByGroup(byGroup);
        setSavedMultiplierByGroup(byGroup);
      } catch {
        if (!cancelled) {
          setMultiplierByGroup({});
          setSavedMultiplierByGroup({});
        }
      }
    })();
    return () => { cancelled = true; };
  }, [visible, accountNumber]);

  const selectedSchedule = useMemo(
    () => feeSchedules.find((fs) => fs.id === selectedId) ?? null,
    [feeSchedules, selectedId]
  );

  // Load the drug-payer matrix ("payer preferences") for the selected PF's category once,
  // cached per category. Drives the checked/unchecked dot next to each payer.
  useEffect(() => {
    const cat = selectedSchedule?.category;
    if (!cat || !accountNumber || prefsLoaded[cat]) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await ApiService.getDrugPayerMatrix(accountNumber, cat);
        if (!cancelled) setPrefsByCategory((prev) => ({ ...prev, [cat]: resp?.drugPayerMatrix || {} }));
      } catch {
        if (!cancelled) setPrefsByCategory((prev) => ({ ...prev, [cat]: {} }));
      } finally {
        if (!cancelled) setPrefsLoaded((prev) => ({ ...prev, [cat]: true }));
      }
    })();
    return () => { cancelled = true; };
  }, [selectedSchedule?.category, accountNumber, prefsLoaded]);

  // Normalized payer -> checked lookup for the selected PF. null until preferences load
  // (so we don't show a misleading red before we know). A payer absent from the matrix is
  // checked by default, matching the app's default-eligible behaviour.
  const payerCheckedLookup = useMemo(() => {
    const cat = selectedSchedule?.category;
    const pid = selectedSchedule?.product_family_id;
    if (!cat || pid == null || !prefsLoaded[cat]) return null;
    const pfRow = (prefsByCategory[cat] || {})[String(pid)] || {};
    const map = new Map<string, boolean>();
    for (const [payer, val] of Object.entries(pfRow)) {
      map.set(normPayer(payer), val !== false);
    }
    return map;
  }, [selectedSchedule, prefsByCategory, prefsLoaded]);

  const hasChanges = useMemo(() => {
    // Compare by value, treating a missing key and $0 as equivalent (both mean "no rate ->
    // Medicare fallback"). Rows we surface for unpriced payers start absent/0, so without
    // this normalization they'd falsely register as changes the moment they render.
    const allKeys = Object.keys(originalRates).concat(Object.keys(editedRates));
    return allKeys.some((k) => (originalRates[k] ?? 0) !== (editedRates[k] ?? 0));
  }, [editedRates, originalRates]);

  const filteredPayers = useMemo(() => {
    // Show every payer that can carry weight, not just the ones already saved on THIS PF.
    // A payer like "Blue Cross Blue Shield (Choice)" may not be a registered master payor
    // group yet still be priced on sibling product families — if this PF's schedule was
    // created without that column, the gap is otherwise invisible and un-fixable here.
    // Union three sources, dedupe by normalized name (saved spelling/value wins):
    //   1. account-wide master payor groups
    //   2. every payer priced on any PF IN THE SAME CATEGORY (surfaces siblings' columns)
    //   3. this PF's own saved rates
    const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, '');
    const byNorm = new Map<string, string>();
    for (const g of masterPayorGroups) {
      const k = norm(g);
      if (k && !byNorm.has(k)) byNorm.set(k, g);
    }
    const cat = selectedSchedule?.category;
    if (cat) {
      for (const fs of feeSchedules) {
        if (fs.category !== cat) continue;
        for (const key of Object.keys(fs.reimbursements || {})) {
          const k = norm(key);
          if (k && !byNorm.has(k)) byNorm.set(k, key);
        }
      }
    }
    for (const saved of Object.keys(editedRates)) {
      const k = norm(saved);
      if (k) byNorm.set(k, saved); // a saved rate on THIS PF wins the display spelling
    }
    const payers = Array.from(byNorm.values()).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
    if (!filterText.trim()) return payers;
    const lower = filterText.toLowerCase();
    return payers.filter((p) => p.toLowerCase().includes(lower));
  }, [editedRates, masterPayorGroups, feeSchedules, selectedSchedule, filterText]);

  // Rebind the payer-preference dot / Medicare-fallback pill tooltips whenever the
  // rendered set changes. Without this they are dead on any render where the Tooltip
  // mounts alongside its targets — e.g. reopening the modal and selecting a product
  // family in a category whose preferences are already cached in state.
  useEffect(() => {
    payerTipRef.current?.updateTargetEvents();
  }, [filteredPayers, payerCheckedLookup, editedRates, selectedId]);

  // Build category groups, filtered by categoryFilter
  const categoryGroups = useMemo((): CategoryGroup[] => {
    const groupMap: Record<string, FeeSchedule[]> = {};
    for (const fs of feeSchedules) {
      // Skip fee schedules with no category — "Uncategorized" entries don't make
      // sense to edit yet, so they're excluded from the options.
      const cat = fs.category;
      if (!cat) continue;
      if (!groupMap[cat]) groupMap[cat] = [];
      groupMap[cat].push(fs);
    }
    const groups = Object.entries(groupMap)
      .map(([category, items]) => ({
        category,
        items: items.sort((a, b) =>
          a.product_family_name.localeCompare(b.product_family_name, undefined, { sensitivity: 'base' })
        ),
      }))
      .sort((a, b) => a.category.localeCompare(b.category, undefined, { sensitivity: 'base' }));

    if (!categoryFilter.trim()) return groups;
    const lower = categoryFilter.toLowerCase();
    return groups.filter((g) => g.category.toLowerCase().includes(lower));
  }, [feeSchedules, categoryFilter]);

  const guardAndSelect = (nextId: number) => {
    if (nextId === selectedId) return;
    const doSelect = () => {
      const next = feeSchedules.find((fs) => fs.id === nextId);
      if (next) {
        setSelectedId(next.id);
        setEditedRates({ ...next.reimbursements });
        setOriginalRates({ ...next.reimbursements });
        setFilterText('');
      }
    };
    if (hasChanges) {
      confirm({
        message: 'You have unsaved changes. Discard them?',
        header: 'Unsaved changes',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Discard',
        rejectLabel: 'Stay',
        danger: true,
        accept: doSelect,
      });
    } else {
      doSelect();
    }
  };

  const changedRateCount = useMemo(() => {
    const keys = new Set(Object.keys(originalRates).concat(Object.keys(editedRates)));
    let n = 0;
    keys.forEach((k) => { if ((originalRates[k] ?? 0) !== (editedRates[k] ?? 0)) n += 1; });
    return n;
  }, [editedRates, originalRates]);
  const medicareLimit = selectedSchedule?.medicare_payment_limit ?? null;

  const handleRateChange = (payer: string, value: number | null) => {
    setEditedRates((prev) => ({ ...prev, [payer]: value ?? 0 }));
  };

  const rateTable = useReactTable({
    ...unpaginated,
    data: filteredPayers,
    columns: rateColumns,
    meta: { view: { editedRates, originalRates, payerCheckedLookup, medicareLimit, onRateChange: handleRateChange } satisfies RateGridView },
    getRowId: (payer) => payer,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleSave = async () => {
    if (!selectedId) return;
    setIsSaving(true);
    try {
      await ApiService.updateFeeSchedule(selectedId, editedRates);
      setFeeSchedules((prev) =>
        prev.map((fs) =>
          fs.id === selectedId ? { ...fs, reimbursements: { ...editedRates } } : fs
        )
      );
      setOriginalRates({ ...editedRates });
      toast.current?.show({
        severity: 'success',
        summary: 'Saved',
        detail: 'Rates saved successfully.',
        life: 3000,
      });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Save Failed',
        detail: handleApiError(err),
        life: 4000,
      });
      setEditedRates({ ...originalRates });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => confirm({
    header: 'Discard changes',
    message: 'Discard your unsaved rate changes?',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Discard',
    rejectLabel: 'Stay',
    danger: true,
    accept: () => setEditedRates({ ...originalRates }),
  });

  const handleResetMultipliers = () => confirm({
    header: 'Discard changes',
    message: 'Discard the multipliers you entered?',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Discard',
    rejectLabel: 'Stay',
    danger: true,
    accept: () => setMultiplierByGroup({ ...savedMultiplierByGroup }),
  });

  const handleDelete = () => {
    if (!selectedSchedule || deleteDialogOpenRef.current) return;
    deleteDialogOpenRef.current = true;
    confirm({
      message: `Clear fee schedule rates for "${selectedSchedule.product_family_name}"?`,
      header: 'Clear fee schedule',
      icon: 'pi pi-exclamation-triangle',
      danger: true,
      accept: async () => {
        deleteDialogOpenRef.current = false;
        try {
          await ApiService.deleteFeeSchedule(selectedSchedule.id);
          // Backend does a soft clear (sets reimbursements to {}) — keep the row in
          // the list so the UI stays consistent with what the DB actually contains.
          setFeeSchedules((prev) =>
            prev.map((fs) =>
              fs.id === selectedSchedule.id ? { ...fs, reimbursements: {} } : fs
            )
          );
          setEditedRates({});
          setOriginalRates({});
          setFilterText('');
          toast.current?.show({
            severity: 'info',
            summary: 'Rates Cleared',
            detail: `Fee schedule rates cleared for "${selectedSchedule.product_family_name}".`,
            life: 3000,
          });
        } catch (err) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: handleApiError(err),
            life: 4000,
          });
        }
      },
      reject: () => { deleteDialogOpenRef.current = false; },
    });
  };

  // Master payer groups eligible for a published-limit multiplier. "Other/Unmapped" is
  // excluded (same as the old per-group UI) — it isn't a real payer group to rate.
  const applyableGroups = useMemo(
    () => masterPayorGroups.filter((g) => g !== 'Other/Unmapped'),
    [masterPayorGroups]
  );

  // Groups whose multiplier is positive AND actually differs from what the server last
  // gave us — the payload for the single Apply All call. Unchanged groups are left out:
  // re-applying them would rewrite byte-identical rates (the backend keeps their
  // materialized rates fresh across Medicare quarters on its own).
  const pendingMultipliers = useMemo(() => {
    const out: Record<string, number> = {};
    for (const g of applyableGroups) {
      const v = multiplierByGroup[g];
      if (quantizeMultiplier(v) <= 0) continue;
      if (quantizeMultiplier(v) === quantizeMultiplier(savedMultiplierByGroup[g])) continue;
      out[g] = v;
    }
    return out;
  }, [applyableGroups, multiplierByGroup, savedMultiplierByGroup]);
  const pendingCount = Object.keys(pendingMultipliers).length;

  // Groups whose saved multiplier was blanked out. Clearing isn't an operation the
  // backend has (non-positive multipliers are ignored), so it can't enable Apply All —
  // but it shouldn't silently read as "no changes" either.
  const clearedCount = useMemo(
    () =>
      applyableGroups.filter(
        (g) =>
          quantizeMultiplier(savedMultiplierByGroup[g]) > 0 &&
          quantizeMultiplier(multiplierByGroup[g]) === 0
      ).length,
    [applyableGroups, multiplierByGroup, savedMultiplierByGroup]
  );

  const hasAnyMultiplier = useMemo(
    () => applyableGroups.some((g) => quantizeMultiplier(multiplierByGroup[g]) > 0),
    [applyableGroups, multiplierByGroup]
  );

  const multiplierView: MultiplierGridView = {
    multiplierByGroup,
    savedMultiplierByGroup,
    setMultiplier: (group, value) => setMultiplierByGroup((prev) => ({ ...prev, [group]: value })),
    disabled: isApplyingAll || isDemoMode,
  };
  // Alphabetical by group, with the sort icon showing (A–Z / Z–A).
  const multiplierTable = useReactTable({
    ...unpaginated,
    data: applyableGroups,
    columns: multiplierColumns,
    meta: { view: multiplierView },
    initialState: { sorting: [{ id: 'group', desc: false }] },
    enableSortingRemoval: false,
    getRowId: (group) => group,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const handleApplyAllMultipliers = () => {
    if (!accountNumber || pendingCount === 0) return;
    const groups = pendingMultipliers;
    confirm({
      message:
        `Set every eligible drug's rate to the Medicare published payment limit × the ` +
        `entered multiplier for ${pendingCount} changed payer group${pendingCount !== 1 ? 's' : ''}? ` +
        `This overwrites existing rates for ${pendingCount === 1 ? 'that group' : 'those groups'}; ` +
        `other groups and later inline edits are preserved.`,
      header: 'Apply published limits',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Apply All',
      rejectLabel: 'Cancel',
      accept: async () => {
        setIsApplyingAll(true);
        try {
          const res = await ApiService.applyPublishedLimitMultiplierBulk(accountNumber, groups);
          // These are now what's applied server-side, so they stop counting as changes
          // and Apply All goes back to disabled until something is edited again.
          setSavedMultiplierByGroup((prev) => ({ ...prev, ...groups }));
          // Refresh Manage Rates so the newly written rates are visible there.
          await loadFeeSchedules();
          toast.current?.show({
            severity: 'success',
            summary: 'Applied',
            detail: res.message || `Applied multipliers for ${pendingCount} group(s).`,
            life: 3500,
          });
        } catch (err) {
          toast.current?.show({
            severity: 'error',
            summary: 'Apply Failed',
            detail: handleApiError(err),
            life: 4000,
          });
        } finally {
          setIsApplyingAll(false);
        }
      },
    });
  };

  const handleTabChange = (index: number) => {
    // Switching away from Upload tab (index 2) while there's a staged upload.
    if (activeTab === 2 && index !== 2 && uploadHasChanges) {
      confirm({
        message: 'You have a staged fee schedule upload. Discard it and switch tabs?',
        header: 'Discard upload',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Discard',
        rejectLabel: 'Stay',
        danger: true,
        accept: () => {
          // Switching away unmounts the Upload tab (TabView renderActiveOnly),
          // which discards the staged upload. Clear the mirror flag too so a
          // later close from another tab doesn't wrongly prompt about staging.
          setUploadHasChanges(false);
          setActiveTab(index);
        },
      });
      return;
    }
    // Switching away from Manage Rates tab (index 1) while there are unsaved rate edits.
    if (activeTab === 1 && index !== 1 && hasChanges) {
      confirm({
        message: 'You have unsaved rate changes. Discard them?',
        header: 'Unsaved changes',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Discard',
        rejectLabel: 'Stay',
        danger: true,
        accept: () => {
          setEditedRates({ ...originalRates });
          setActiveTab(index);
        },
      });
      return;
    }
    setActiveTab(index);
  };

  const handleHide = () => {
    const dirty = hasChanges || uploadHasChanges;
    if (dirty) {
      confirm({
        message: uploadHasChanges && !hasChanges
          ? 'You have a staged fee schedule upload. Discard it and close?'
          : 'You have unsaved changes. Discard them and close?',
        header: 'Unsaved changes',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Discard & Close',
        rejectLabel: 'Stay',
        danger: true,
        accept: onHide,
      });
    } else {
      onHide();
    }
  };

  return (
    <Dialog
      header={<ModalHeading title="Fee schedules" subtitle="Reimbursement per billing unit for each product and master payer group. Groups without a rate use the Medicare published limit." />}
      visible={visible}
      style={{ width: '70vw', maxWidth: '1000px', height: '80vh' }}
      contentStyle={{ overflow: 'hidden', padding: 0, display: 'flex', flexDirection: 'column' }}
      className="fee-schedule-dialog"
      contentClassName="flex-auto min-h-0"
      modal
      onHide={handleHide}
      // One footer bar for each editable tab, laid out like Payer groupings: status on the left, actions on the right.
      footer={
        activeTab === 0 ? (
          <div className="setup-modal-footer flex align-items-center u-gap-2 w-full">
            <span className="setup-footer-msg text-xs text-secondary">
              {pendingCount > 0
                ? `${pendingCount} group${pendingCount !== 1 ? 's' : ''} will be updated across every eligible drug`
                : clearedCount > 0
                  ? 'Clearing a multiplier has no effect — rates stay as last applied'
                  : hasAnyMultiplier
                    ? 'No changes to apply'
                    : 'No multipliers entered'}
            </span>
            <span className="flex-1" />
            {(pendingCount > 0 || clearedCount > 0) && (
              <Button variant="primary-outline" onClick={handleResetMultipliers} disabled={isApplyingAll}>Reset</Button>
            )}
            <Tooltip target=".demo-tip-fs-applyall" position="top" />
            <span
              className={`inline-flex${isDemoMode ? ' demo-tip-fs-applyall' : ''}`}
              data-pr-tooltip={isDemoMode ? 'Read-only demo account' : undefined}
            >
              <Button
                loading={isApplyingAll}
                onClick={handleApplyAllMultipliers}
                // Demo accounts are read-only server-side (423).
                disabled={isApplyingAll || isDemoMode || pendingCount === 0}
              ><i className="pi pi-check" aria-hidden="true" />Apply to all drugs</Button>
            </span>
          </div>
        ) : activeTab === 1 ? (
          <div className="setup-modal-footer flex align-items-center u-gap-2 w-full">
            <span className="setup-footer-msg text-xs text-secondary">{changedRateCount > 0 ? `${changedRateCount} unsaved change${changedRateCount === 1 ? '' : 's'}` : 'No unsaved changes'}</span>
            <span className="flex-1" />
            <Button variant="primary-outline" onClick={handleReset} disabled={!hasChanges || isSaving}><i className="pi pi-undo" aria-hidden="true" />Reset</Button>
            <Tooltip target=".demo-tip-fs-save" position="top" />
            <span
              className={`inline-flex${isDemoMode ? ' demo-tip-fs-save' : ''}`}
              data-pr-tooltip={isDemoMode ? 'Read-only demo account' : undefined}
            >
              <Button
                loading={isSaving}
                onClick={handleSave}
                // Demo accounts are read-only server-side (423).
                disabled={isLoading || isSaving || !hasChanges || isDemoMode}
              ><i className="pi pi-save" aria-hidden="true" />Save rates</Button>
            </span>
          </div>
        ) : activeTab === 2 && uploadFooter ? (
          <div className="setup-modal-footer flex align-items-center u-gap-2 w-full">
            <Button variant="secondary-text" onClick={uploadFooter.onUploadDifferent} disabled={uploadFooter.isCommitting}><i className="pi pi-upload" aria-hidden="true" />Upload different file</Button>
            <span className="flex-1" />
            <Button variant="primary-outline" onClick={uploadFooter.onDiscard} disabled={uploadFooter.isCommitting}>Discard</Button>
            {/* The hint sits on this span rather than on the Button: a disabled button gets no hover, and
                PrimeReact's showOnDisabled re-parents the button outside React and crashes on unmount. */}
            <Tooltip target=".fs-upload-apply-tip" position="top" />
            <span
              className={`inline-flex${uploadFooter.applyHint ? ' fs-upload-apply-tip' : ''}`}
              data-pr-tooltip={uploadFooter.applyHint}
            >
              <Button loading={uploadFooter.isCommitting} onClick={uploadFooter.onApply} disabled={!uploadFooter.canApply}>
                <i className="pi pi-check" aria-hidden="true" />{uploadFooter.applyLabel}
              </Button>
            </span>
          </div>
        ) : (
          <div className="setup-modal-footer flex align-items-center justify-content-end w-full"><Button variant="primary-outline" onClick={handleHide}>Close</Button></div>
        )
      }
    >
      <TabView className="fee-schedule-tabs flex flex-column flex-1 min-h-0" panelContainerClassName="fee-schedule-tab-panels flex-1 min-h-0 overflow-hidden flex flex-column" activeIndex={activeTab} onTabChange={(e) => handleTabChange(e.index)}>
        <TabPanel header="Published limits" contentClassName="fee-tab flex-1 min-h-0 flex flex-column fee-tab-padded">
          <div className="fs-multiplier-tab flex-1 min-h-0 flex flex-column">
            <Tooltip target=".multiplier-locked" position="top" />
            <p className="fs-multiplier-intro u-mt-0 u-mx-0 u-mb-2 text-xs text-secondary">
              Rate = Medicare published limit &times; multiplier, for every drug in the group. Individual rates stay editable in <strong>Manage rates</strong>.
            </p>

            <AppTable
              isBordered={false}
              isStickyHeader
              size="sm"
              ariaLabel="Published-limit multipliers"
              table={multiplierTable}
              className="siq-fill-table siq-dense-inputs"
              rowClassName={({ rowData: group }) => (isMultiplierChanged(group, multiplierView) ? 'is-changed' : '')}
              messages={{ emptyStateText: 'No master payer groups are defined for this account yet.' }}
            />
          </div>
        </TabPanel>

        <TabPanel header="Manage rates" contentClassName="fee-tab flex-1 min-h-0 flex flex-column">
          {isLoading && (
            <div className="fee-schedule-status flex align-items-center justify-content-center u-gap-3 text-secondary u-p-5">
              <ProgressSpinner style={{ width: '36px', height: '36px' }} strokeWidth="6" />
            </div>
          )}

          {!isLoading && error && (
            <div className="fee-schedule-status flex align-items-center justify-content-center u-gap-3 u-p-5 text-danger">{error}</div>
          )}

          {!isLoading && !error && feeSchedules.length === 0 && (
            <div className="fee-schedule-status flex align-items-center u-gap-3 text-secondary u-p-5 fee-schedule-empty-state flex flex-column justify-content-center text-center u-gap-3 u-py-9 u-px-5 min-h-15rem">
              <i className="pi pi-inbox" style={{ fontSize: '1.75rem', color: 'var(--siq-icon-muted)' }} />
              <p className="u-m-0 font-semibold text-secondary text-sm">No fee schedules set up for this account yet</p>
              <p className="fee-schedule-empty-sub u-m-0 text-tertiary text-xs max-w-30rem leading-normal">
                To add rates, switch to the <strong>Upload Fee Schedule</strong> tab and upload your
                fee schedule. Once uploaded, your product families will appear here to review and edit.
              </p>
              <Button onClick={() => handleTabChange(2)} className="u-mt-1"><i className="pi pi-upload" aria-hidden="true" />Go to Upload Fee Schedule</Button>
            </div>
          )}

          {!isLoading && !error && feeSchedules.length > 0 && (
            <div className="fee-schedule-layout">
              {/* ---- Sidebar: category-grouped tree ---- */}
              <div className="fee-schedule-sidebar">
                <div className="u-py-2 u-px-3 border-b-1 border-default">
                  <InputText
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    placeholder="Search therapeutic areas"
                    className="w-full"
                  />
                </div>

                <div className="fee-schedule-tree flex-1 overflow-y-auto u-py-1 u-px-0">
                  {categoryGroups.length === 0 && (
                    <div className="fee-schedule-status flex align-items-center justify-content-center u-gap-3 text-secondary u-p-5" style={{ padding: '0.4375rem 0' }}>
                      No categories match.
                    </div>
                  )}
                  {/* One Accordion tab per area; several can be open at once. */}
                  <Accordion
                    multiple
                    activeIndex={categoryGroups.flatMap((group, i) => (expandedCategories.has(group.category) ? [i] : []))}
                    onTabChange={(event) => {
                      // The open tabs among the areas shown, plus any open area the search is hiding right now.
                      const opened = (Array.isArray(event.index) ? event.index : [event.index]).map((i) => categoryGroups[i]?.category).filter(Boolean) as string[];
                      const hidden = Array.from(expandedCategories).filter((c) => !categoryGroups.some((g) => g.category === c));
                      setExpandedCategories(new Set(hidden.concat(opened)));
                    }}
                  >
                    {categoryGroups.map((group) => (
                      <AccordionTab
                        key={group.category}
                        header={<span className="flex align-items-center u-gap-2 min-w-0 w-full"><span className="flex-1 min-w-0 text-truncate">{group.category}</span><span className="text-2xs font-normal text-tertiary">{group.items.length}</span></span>}
                      >
                        <div className="flex flex-column">
                          {group.items.map((fs) => (
                            <Button
                              key={fs.id}
                              variant="secondary-text"
                              className={`fee-schedule-item w-full justify-content-start text-xs${fs.id === selectedId ? ' bg-surface-selected font-semibold' : ''}`}
                              aria-current={fs.id === selectedId ? 'true' : undefined}
                              onClick={() => guardAndSelect(fs.id)}
                            >
                              <span className="text-truncate">{fs.product_family_name}</span>
                            </Button>
                          ))}
                        </div>
                      </AccordionTab>
                    ))}
                  </Accordion>
                </div>
              </div>

              {/* ---- Detail panel ---- */}
              <div className="flex flex-column u-py-3 u-px-4 overflow-hidden">
                {/* Mounted unconditionally so it is past its bind-skipping first
                    render before any dot or fallback note exists; see payerTipRef. */}
                <Tooltip position="top" ref={payerTipRef} target=".payer-pref-dot,.fee-fallback-note" />
                {!selectedSchedule && (
                  <div className="fee-schedule-empty-detail flex flex-column align-items-center justify-content-center h-full u-gap-3 text-tertiary text-xs">
                    <i className="pi pi-arrow-left" style={{ fontSize: '1.0938rem', color: 'var(--siq-icon-muted)' }} />
                    <span>Select a product family to view its rates</span>
                  </div>
                )}
                {selectedSchedule && (
                  <>
                    {/* One line (name and limit, then the filter) so short laptop screens keep room for rates. */}
                    <div className="u-mb-3 flex justify-content-between align-items-center u-gap-3 flex-wrap">
                      <div className="fee-schedule-detail-title flex align-items-baseline u-gap-2 flex-wrap min-w-0">
                        <h3 className="u-m-0 text-md font-semibold text-default">{selectedSchedule.product_family_name}</h3>
                        {medicareLimit !== null && <span className="text-xs text-tertiary">Medicare limit ${medicareLimit.toFixed(4)}</span>}
                      </div>
                      {/* Clearing a family's rates sits with that family, not beside Save (it asks before clearing). */}
                      <Tooltip target=".demo-tip-fs-clear" position="top" />
                      <span
                        className={`inline-flex u-ml-auto${isDemoMode ? ' demo-tip-fs-clear' : ''}`}
                        data-pr-tooltip={isDemoMode ? 'Read-only demo account' : undefined}
                      >
                        <Button variant="danger-text" onClick={handleDelete} disabled={isLoading || isSaving || isDemoMode || Object.keys(selectedSchedule.reimbursements).length === 0} /* Demo accounts are read-only server-side (423). */><i className="pi pi-trash" aria-hidden="true" />Clear all rates</Button>
                      </span>
                      <InputSearch
                        className="fee-schedule-filter w-12rem flex-none"
                        leftIcon={<i className="pi pi-search" aria-hidden="true" />}
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        onClear={() => setFilterText('')}
                        clearButtonAriaLabel="Clear payer group filter"
                        placeholder="Search groups"
                        aria-label="Filter payer groups"
                      />
                    </div>

                    <AppTable
                      isBordered={false}
                      isStickyHeader
                      size="sm"
                      ariaLabel={`Rates for ${selectedSchedule.product_family_name}`}
                      table={rateTable}
                      className="siq-fill-table siq-dense-inputs"
                      rowClassName={({ rowData: payer }) => (isRateChanged(payer, tableView<RateGridView, string>(rateTable)) ? 'is-changed' : '')}
                      messages={{ emptyStateText: filterText.trim() ? 'No payers match the filter.' : 'No rates defined for this product family.' }}
                    />
                  </>
                )}
              </div>
            </div>
          )}
        </TabPanel>

        <TabPanel header="Upload fee schedule" contentClassName="fee-tab flex-1 min-h-0 flex flex-column fee-tab-padded">
          <FeeScheduleUpload
            accountNumber={accountNumber}
            masterPayorGroups={masterPayorGroups}
            existingFeeSchedules={feeSchedules}
            onCommitted={loadFeeSchedules}
            onHasStagedChanges={setUploadHasChanges}
            onFooterChange={setUploadFooter}
          />
        </TabPanel>
      </TabView>
    </Dialog>
  );
};

export default FeeScheduleModal;
